import { z } from "zod";

/**
 * Answer-Quality Policy — the declarative, config-as-data definition of the
 * cross-tier answer-quality / verification GATE. A sysadmin edits a ROW; the
 * in-code AnswerQualityGateFilter reads it and decides whether to downgrade,
 * flag, or block an answer (and what confidence ceiling an UNVERIFIED answer may
 * surface). NO thresholds or verification policy live in pipeline code.
 *
 * This is the founder's "improve answer quality with a wrapper at the sysadmin
 * level" ask: the system must VERIFY whether an answer is grounded — across ALL
 * tiers (library docs, MCP tool results, web, model knowledge), not just the
 * library tier that faithfulness already checks — and must NEVER stamp "High
 * confidence" on data it never verified.
 *
 * DESIGN — CLOSED & ENUMERATED (same principle as AnswerPolicyContentSchema):
 * every knob is typed and finite. Air-gap: every model touchpoint the gate uses
 * is a LOCAL sidecar / Ollama (faithfulness NLI + the optional second-model
 * reviewer) — no external network (CLAUDE.md Rule #12).
 */

export const QualityStrictnessSchema = z.enum(["lenient", "standard", "strict"]);
export type QualityStrictness = z.infer<typeof QualityStrictnessSchema>;

/** What the gate does when a check trips. downgrade = cap confidence + warn. */
export const QualityOnFailSchema = z.enum(["downgrade", "flag", "block"]);
export type QualityOnFail = z.infer<typeof QualityOnFailSchema>;

/** Per-check toggles. Heavy checks (secondModelReview) default OFF. */
export const QualityChecksSchema = z.object({
  /** Verify the answer against EVERY present source tier, not just library. */
  crossTierGroundedness: z.boolean().default(true),
  /** Flag numeric/source self-contradiction in the answer. */
  selfConsistency: z.boolean().default(true),
  /** Penalize hedging / ungrounded-entity density on factual queries. */
  uncertainty: z.boolean().default(true),
  /** Optional local second-model review OF THE ANSWER (Maximum-profile twin). */
  secondModelReview: z.boolean().default(false),
  /** Cheap guards: cites a [Source N] that doesn't exist, tool error stated as success. */
  sourceSanity: z.boolean().default(true),
});
export type QualityChecks = z.infer<typeof QualityChecksSchema>;

export const PerTierQualitySchema = z.object({
  /** Library answers need faithfulness >= verifyFloor to count as VERIFIED. */
  library: z
    .object({ verifyFloor: z.number().min(0).max(1).default(0.7) })
    .default({ verifyFloor: 0.7 }),
  /** MCP tool-result groundedness floor (when the NLI sidecar is present). */
  mcp: z
    .object({ verifyFloor: z.number().min(0).max(1).default(0.6) })
    .default({ verifyFloor: 0.6 }),
  /** Web-snippet groundedness floor. */
  web: z
    .object({ verifyFloor: z.number().min(0).max(1).default(0.6) })
    .default({ verifyFloor: 0.6 }),
  /** Model-knowledge (amber) is inherently unverified — can never read "High". */
  ai: z.object({ neverHigh: z.boolean().default(true) }).default({ neverHigh: true }),
});
export type PerTierQuality = z.infer<typeof PerTierQualitySchema>;

export const SecondModelSchema = z.object({
  /** Local model brand for the reviewer; null = MODEL_GUARDRAIL_SECONDARY default. */
  brandId: z.string().nullable().default(null),
  timeoutMs: z.number().int().positive().max(5000).default(400),
  /** Strict: a reviewer timeout/error is treated as a fail (never silent pass). */
  failClosed: z.boolean().default(true),
});
export type SecondModel = z.infer<typeof SecondModelSchema>;

export const AnswerQualityPolicyContentSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  /** Master on/off. When false the gate no-ops (raw heuristic confidence stands). */
  enabled: z.boolean().default(true),
  strictness: QualityStrictnessSchema.default("standard"),
  // Explicit full defaults (not `.default({})`): Zod's `.default()` input type
  // strips `undefined` from each property, so an empty `{}` no longer satisfies it.
  checks: QualityChecksSchema.default({
    crossTierGroundedness: true,
    selfConsistency: true,
    uncertainty: true,
    secondModelReview: false,
    sourceSanity: true,
  }),
  perTier: PerTierQualitySchema.default({
    library: { verifyFloor: 0.7 },
    mcp: { verifyFloor: 0.6 },
    web: { verifyFloor: 0.6 },
    ai: { neverHigh: true },
  }),
  secondModel: SecondModelSchema.default({ brandId: null, timeoutMs: 400, failClosed: true }),
  onFail: QualityOnFailSchema.default("downgrade"),
  /**
   * 0-100 ceiling applied to an answer the system could NOT verify (driven by
   * unverified MCP/web/AI sources without verified library backing). Default 60
   * → "Medium" on the chip: honest "live source, not document-verified" — never
   * "High". A CONTRADICTED answer (faithfulness/second-model failed) is capped
   * harder (to contradictedCap) below this.
   */
  confidenceCap: z.number().int().min(0).max(100).default(60),
  /** Harder ceiling for an answer that verification actively CONTRADICTED. */
  contradictedCap: z.number().int().min(0).max(100).default(30),
});
export type AnswerQualityPolicyContent = z.infer<typeof AnswerQualityPolicyContentSchema>;

/** The in-code default (terminal fallback when no DB row / table unmigrated). */
export const DEFAULT_ANSWER_QUALITY_POLICY: AnswerQualityPolicyContent =
  AnswerQualityPolicyContentSchema.parse({});
